'use client';

import { faCircleCheck, faCircleInfo, faSpinner, faTrash, faTriangleExclamation, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactNode } from 'react';

import { cn } from '../../../lib/utils';
import { Avatar } from '../../ui/avatar';

// Each product shows the card in its own color: ladder leagues purple (the default), player-facing ladder leagues
// pink (the ladder-league rose, #F4C7CF / #7E2638), tournaments blue, team leagues orange. The rose is not in the
// shared Tailwind palette, so its shades are spelled out as arbitrary values.
export type PlayerSelectCardColor = 'purple' | 'blue' | 'pink' | 'orange' | 'gray';

const COLOR_CLASSES: Record<PlayerSelectCardColor, { card: string; selected: string; button: string }> = {
	purple: {
		card: 'border-violet-200 bg-violet-25',
		selected: 'ring-2 ring-violet-500',
		button: 'bg-violet-600 text-white hover:bg-violet-700 focus-visible:ring-violet-300'
	},
	blue: {
		card: 'border-blue-200 bg-blue-25',
		selected: 'ring-2 ring-blue-500',
		button: 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-300'
	},
	pink: {
		card: 'border-[#F4C7CF] bg-[#FDF5F7]',
		selected: 'ring-2 ring-[#7E2638]',
		button: 'bg-[#F4C7CF] text-[#7E2638] hover:bg-[#EBABB7] focus-visible:ring-[#EBABB7]'
	},
	orange: {
		card: 'border-darkorange-200 bg-darkorange-25',
		selected: 'ring-2 ring-darkorange-400',
		button: 'bg-orange-500 text-white hover:bg-orange-700 focus-visible:ring-orange-400'
	},
	gray: {
		card: 'border-gray-200 bg-white',
		selected: 'ring-2 ring-gray-500',
		button: 'bg-gray-700 text-white hover:bg-gray-800 focus-visible:ring-gray-300'
	}
};

export type PlayerSelectCardTone = 'error' | 'gray' | 'info' | 'success' | 'warning';

const TONE_CLASSES: Record<PlayerSelectCardTone, string> = {
	error: 'border-error-200 bg-error-50 text-error-700',
	gray: 'border-gray-200 bg-gray-100 text-gray-500',
	info: 'border-primary-200 bg-primary-25 text-primary-700',
	success: 'border-success-200 bg-success-50 text-success-700',
	warning: 'border-warning-200 bg-warning-50 text-warning-700'
};

const TONE_ICONS = {
	error: faXmark,
	gray: faCircleInfo,
	info: faCircleInfo,
	success: faCircleCheck,
	warning: faTriangleExclamation
};

// A short chip under the ratings: a criterion the player does not meet, like a wrong age or rating (`error`),
// or why they cannot be picked (`gray`).
export interface PlayerSelectCardNote {
	key: string;
	label: string;
	tone: PlayerSelectCardTone;
}

// A full-width strip along the bottom of the card, for a sentence-long reason that does not fit a chip — usually
// the server's "why this player cannot be picked" message.
export interface PlayerSelectCardBanner {
	key: string;
	label: ReactNode;
	tone: PlayerSelectCardTone;
}

export interface PlayerSelectCardRating {
	// Shown in brackets before a numeric value, e.g. "D" -> "(D) 4.25". Left out when empty.
	label: string;
	// A number is rounded to `ratingDecimals`, and a missing or zero one shows as "NR". Text is shown as given,
	// for apps whose server already formats the rating.
	value?: number | string | null;
	// Text ratings count as rated unless this says otherwise, e.g. a server's "--" for an unrated player.
	rated?: boolean;
}

export interface PlayerSelectCardAction {
	label: ReactNode;
	onClick: () => void;
	disabled?: boolean;
	isLoading?: boolean;
	testId?: string;
	// `muted` is the gray button for a player who is already picked ("Selected").
	variant?: 'primary' | 'muted';
}

export interface PlayerSelectCardProps {
	name: string;
	// Shown right after the name, e.g. "- Free agent".
	nameSuffix?: ReactNode;
	avatarUrl?: string | null;
	// Under the photo. Pass a whole "City, State" line as `state` when that is what the server sends.
	state?: string | null;
	country?: string | null;
	// Shown as given, so a hidden age can arrive as "**". The age line is left off when both are missing, and shows
	// whichever of the two is there.
	age?: string | number | null;
	// "Male" / "M" and "Female" / "F" both show as "Gender: M" / "Gender: F"; anything else (e.g. "**") as given.
	gender?: string | null;
	// Email and phone are shown exactly as given: masking is up to the caller (or its server), who knows the viewer.
	email?: string | null;
	phone?: string | null;
	// Phone, then email, then age and gender, instead of age, email, phone (ladder leagues).
	phoneFirst?: boolean;
	// Extra classes for the email and phone lines, e.g. a monospace font so server-masked "•••" lines up.
	contactClassName?: string;
	isMember?: boolean;
	memberLabel?: ReactNode;
	// Left off when not given.
	ratings?: PlayerSelectCardRating[];
	ratingDecimals?: number;
	notes?: PlayerSelectCardNote[];
	banners?: PlayerSelectCardBanner[];
	// Extra lines between the phone and the ratings, e.g. a DUPR ID.
	extraDetails?: ReactNode;
	color?: PlayerSelectCardColor;
	selected?: boolean;
	// The built-in button, colored to match the card.
	action?: PlayerSelectCardAction;
	// A button of the app's own, e.g. "Change" / "Edit" on a summary card. Shown before the built-in button when both
	// are given.
	actionSlot?: ReactNode;
	hideAction?: boolean;
	// A trash icon in the top-right corner, for the "selected player" summary.
	onRemove?: () => void;
	// Icons or small buttons of the app's own in the top-right corner, e.g. a profile link and "Remove access".
	headerActions?: ReactNode;
	// Extra content inside the card under the details, e.g. a settings row that belongs to this player.
	footer?: ReactNode;
	className?: string;
}

// Sizes shared by the card and its loading skeleton, so the skeleton lines up with what replaces it. Phones get a
// smaller photo, name and padding; from `sm` up the card uses the full desktop sizes.
const LAYOUT = {
	row: 'flex w-full gap-4 p-3 sm:gap-6 sm:p-4',
	// Wide enough for long single words in state names ("Massachusetts", "Federacija Bosna i Hercegovina").
	side: 'flex w-20 shrink-0 flex-col items-center gap-2 sm:w-24',
	avatar: 'h-16 w-16 sm:h-20 sm:w-20',
	body: 'flex min-w-0 flex-1 flex-col gap-2',
	// Name, age, email and phone take the full width; the pick button shares a row only with the extra lines (DUPR
	// ID, format rating) and the ratings, level with the last of them.
	details: 'flex min-w-0 flex-col gap-2',
	ratingsRow: 'flex items-end justify-between gap-3'
};

function formatRating(rating: PlayerSelectCardRating, decimals: number) {
	const prefix = rating.label ? `(${rating.label}) ` : '';
	if (typeof rating.value === 'string') return { text: `${prefix}${rating.value || 'NR'}`, isRated: rating.rated ?? !!rating.value };
	const isRated = !!rating.value && rating.value > 0;
	return { text: `${prefix}${isRated ? rating.value!.toFixed(decimals) : 'NR'}`, isRated };
}

// The photo, or the player's initials when there is none or it fails to load.
function CardAvatar({ name, src }: { name: string; src?: string | null }) {
	return <Avatar name={name} alt={name || 'Player'} src={src || undefined} className={LAYOUT.avatar} textClassName="text-xl sm:text-2xl" />;
}

function toGenderLetter(gender: string) {
	const normalized = gender.trim().toUpperCase();
	if (normalized === 'M' || normalized === 'MALE') return 'M';
	if (normalized === 'F' || normalized === 'FEMALE') return 'F';
	return gender;
}

/**
 * The player tile in every "search and pick a player" panel: photo and where they live on the left; name, age
 * and gender, email, phone, ratings and any criteria notes on the right; the pick button bottom right; and any
 * sentence-long reason the player cannot be picked along the bottom.
 *
 * It only displays what it is given, so any app can feed it its own player shape, already masked for the viewer.
 */
export function PlayerSelectCard({
	name,
	nameSuffix,
	avatarUrl,
	state,
	country,
	age,
	gender,
	email,
	phone,
	phoneFirst,
	contactClassName,
	isMember,
	memberLabel = 'Member',
	ratings,
	ratingDecimals = 2,
	notes,
	banners,
	extraDetails,
	color = 'purple',
	selected = false,
	action,
	actionSlot,
	hideAction,
	onRemove,
	headerActions,
	footer,
	className
}: PlayerSelectCardProps) {
	const colors = COLOR_CLASSES[color];
	const hasAge = age !== undefined && age !== null && age !== '';
	const genderLabel = gender ? `Gender: ${toGenderLetter(gender)}` : null;
	const ageLine = [hasAge ? `Age: ${age}` : null, genderLabel].filter(Boolean).join(' | ');
	const isMutedAction = action?.variant === 'muted';
	const showAction = !hideAction && (!!actionSlot || !!action);

	return (
		<div
			className={cn(
				'w-full overflow-hidden rounded-lg border text-start text-sm hover:border-gray-300',
				colors.card,
				{ [colors.selected]: selected },
				className
			)}
			data-selected={selected}
		>
			<div className={cn(LAYOUT.row, { 'sm:pb-3': !!banners?.length })}>
				<div className={LAYOUT.side}>
					<CardAvatar name={name} src={avatarUrl} />
					<div className="flex w-full flex-col gap-1 text-center">
						{state ? <p className="break-words text-xs text-gray-800">{state}</p> : null}
						{country ? <p className="text-xs text-gray-400">{country}</p> : null}
					</div>
				</div>
				<div className={LAYOUT.body}>
					<div className="flex justify-between gap-2">
						<h5 className="break-words text-sm font-semibold text-gray-900 sm:text-base">
							{name}
							{nameSuffix ? <span className="font-normal text-gray-500"> {nameSuffix}</span> : null}
						</h5>
						<div className="flex shrink-0 items-start gap-3">
							{isMember ? <p className="text-sm font-medium text-green-600">{memberLabel}</p> : null}
							{headerActions}
							{onRemove ? (
								<button type="button" onClick={onRemove} aria-label="Remove player" className="text-gray-400 hover:text-error-600">
									<FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
								</button>
							) : null}
						</div>
					</div>
					<div className={LAYOUT.details}>
						{!phoneFirst && ageLine ? <p className="text-xs text-gray-800">{ageLine}</p> : null}
						{phoneFirst && phone ? <p className={cn('text-xs text-gray-800', contactClassName)}>{phone}</p> : null}
						{email ? <p className={cn('break-all text-xs text-gray-800', contactClassName)}>{email}</p> : null}
						{!phoneFirst && phone ? <p className={cn('text-xs text-gray-800', contactClassName)}>{phone}</p> : null}
						{phoneFirst && ageLine ? <p className="text-xs text-gray-800">{ageLine}</p> : null}
						{extraDetails || !!ratings?.length || showAction ? (
							<div className={LAYOUT.ratingsRow}>
								<div className="flex min-w-0 flex-col gap-2">
									{extraDetails}
									{!!ratings?.length && (
										<div className="flex flex-wrap gap-2">
											{ratings.map((rating) => {
												const { text, isRated } = formatRating(rating, ratingDecimals);
												return (
													<p
														key={rating.label || text}
														className={cn('text-xs', isRated ? 'text-gray-800' : 'text-gray-500')}
													>
														{text}
													</p>
												);
											})}
										</div>
									)}
								</div>
								{showAction ? (
									<div className="ml-auto flex shrink-0 items-end gap-2">
										{actionSlot}
										{action ? (
											<button
												type="button"
												onClick={action.onClick}
												disabled={action.disabled || action.isLoading}
												data-test-id={action.testId}
												className={cn(
													'min-w-[56px] whitespace-nowrap rounded-lg px-3 py-1.5 text-center text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50',
													{
														[colors.button]: !isMutedAction,
														'bg-gray-100 text-gray-500 focus-visible:ring-gray-300': isMutedAction
													}
												)}
											>
												{action.isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : action.label}
											</button>
										) : null}
									</div>
								) : null}
							</div>
						) : null}
						{!!notes?.length && (
							<div className="flex flex-wrap items-center gap-1.5">
								{notes.map((note) => (
									<span
										key={note.key}
										className={cn(
											'inline-flex items-center gap-1 rounded-full border py-0.5 pl-1.5 pr-2 text-[11px] font-medium leading-4',
											TONE_CLASSES[note.tone]
										)}
									>
										<FontAwesomeIcon icon={TONE_ICONS[note.tone]} className="h-2.5 w-2.5" />
										{note.label}
									</span>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
			{footer ? <div className="px-3 pb-3 sm:px-4 sm:pb-4">{footer}</div> : null}
			{banners?.map((banner) => (
				<div
					key={banner.key}
					className={cn(
						'flex flex-col items-center gap-1 border-t px-3 py-2 text-center text-xs font-medium leading-[18px] sm:px-4',
						TONE_CLASSES[banner.tone]
					)}
				>
					{banner.label}
				</div>
			))}
		</div>
	);
}

// One skeleton line, as tall as the text line it stands in for, with the bar centred in it.
function SkeletonLine({ className, lineClassName = 'h-4' }: { className: string; lineClassName?: string }) {
	return (
		<div className={cn('flex items-center', lineClassName)}>
			<div className={cn('h-3 rounded-full bg-gray-200', className)} />
		</div>
	);
}

/**
 * The loading placeholder for `PlayerSelectCard`: same border, tint, padding, photo, columns and line heights, so
 * the list does not jump when the results arrive. Render a few of them while a player search is loading.
 */
export function PlayerSelectCardSkeleton({
	color = 'purple',
	showAction = true,
	className
}: {
	color?: PlayerSelectCardColor;
	showAction?: boolean;
	className?: string;
}) {
	return (
		<div className={cn('w-full animate-pulse overflow-hidden rounded-lg border', COLOR_CLASSES[color].card, className)} aria-hidden="true">
			<div className={LAYOUT.row}>
				<div className={LAYOUT.side}>
					<div className={cn('shrink-0 rounded-full bg-gray-200', LAYOUT.avatar)} />
					<div className="flex w-full flex-col items-center gap-1">
						<SkeletonLine className="w-14" />
						<SkeletonLine className="w-8" />
					</div>
				</div>
				<div className={LAYOUT.body}>
					{/* The name: text-sm on phones, text-base from `sm` up. */}
					<SkeletonLine className="h-4 w-32 sm:w-40" lineClassName="h-5 sm:h-6" />
					<div className={LAYOUT.details}>
						<SkeletonLine className="w-24" />
						<SkeletonLine className="w-40" />
						<SkeletonLine className="w-20" />
						<div className={LAYOUT.ratingsRow}>
							<div className="flex gap-2">
								<SkeletonLine className="w-12" />
								<SkeletonLine className="w-12" />
								<SkeletonLine className="w-12" />
							</div>
							{/* Same height as the pick button (py-1.5 + one text-xs line). */}
							{showAction ? <div className="ml-auto h-7 w-14 shrink-0 rounded-lg bg-gray-200" /> : null}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
